import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, HeartCrack } from 'lucide-react';
import { useArenaStore } from '../../../store/useArenaStore';
import { GameHudProps } from '../../../presentation/GamePresentationTypes';
import { isHeartsJackOfDiamonds } from '../HeartsDeck';

export const HeartsTableHud: React.FC<GameHudProps> = ({
  heartsBroken: propHeartsBroken,
  isPlayPhase = false,
  passingDirection: propPassingDirection,
  seatPoints: propSeatPoints,
  gameState,
}) => {
  const storeHeartsBroken = useArenaStore((s) => s.effectiveGameState?.heartsBroken);
  const storePassingDirection = useArenaStore((s) => s.effectiveGameState?.passingDirection);
  const effectiveHeartsBroken = Boolean(propHeartsBroken ?? storeHeartsBroken ?? gameState?.heartsBroken);
  const passingDir = propPassingDirection || storePassingDirection || gameState?.passingDirection || 'LEFT';

  // Read authoritative seat points taken in current hand and cumulative player scores
  const rawSeatPoints = Array.isArray(propSeatPoints)
    ? propSeatPoints
    : (Array.isArray(gameState?.seatPoints)
      ? gameState.seatPoints
      : (Array.isArray((gameState as any)?.pointsTaken)
        ? (gameState as any).pointsTaken
        : [0, 0, 0, 0]));

  const isJackOfDiamondsEnabled = Boolean(
    gameState?.jackOfDiamonds ??
    (gameState?.gameExt as any)?.jackOfDiamonds ??
    gameState?.gameConfig?.tableOptions?.some(o => o.id === 'jackOfDiamonds' && (o.defaultValue === true || (o as any).value === true)) ??
    gameState?.gameConfig?.scoring?.pointCards?.some(p => (p.suit === 'D' || p.suit === 'Diamonds') && (p.rank === 'J' || p.cardId === 'D_J'))
  );

  // Ensure points strictly accumulate throughout the hand by reconciling with completedTricks
  const seatPoints = React.useMemo(() => {
    const pts = [
      Number(rawSeatPoints[0]) || 0,
      Number(rawSeatPoints[1]) || 0,
      Number(rawSeatPoints[2]) || 0,
      Number(rawSeatPoints[3]) || 0,
    ];
    if (gameState?.completedTricks && gameState.completedTricks.length > 0) {
      const trickPts = [0, 0, 0, 0];
      for (const trick of gameState.completedTricks) {
        const winner = Number(trick.winningSeat);
        if (winner >= 0 && winner < 4) {
          for (const tc of (trick.cards || [])) {
            const c = tc?.card;
            if (!c) continue;
            if (c.suit === 'H' || c.suit === 'Hearts') {
              trickPts[winner] += 1;
            } else if ((c.suit === 'S' || c.suit === 'Spades') && (c.rank === 'Q' || c.id === 'S_Q' || c.id === 'QS')) {
              trickPts[winner] += 13;
            } else if (isJackOfDiamondsEnabled && isHeartsJackOfDiamonds(c)) {
              trickPts[winner] -= 10;
            }
          }
        }
      }
      for (let i = 0; i < 4; i++) {
        pts[i] = rawSeatPoints[i] !== undefined && rawSeatPoints[i] !== 0 ? rawSeatPoints[i] : trickPts[i];
      }
    }
    return pts;
  }, [rawSeatPoints, gameState?.completedTricks, isJackOfDiamondsEnabled]);

  const isQueenPlayed = Boolean(
    gameState?.queenOfSpadesPlayed ??
    ((gameState as any)?.queenOfSpadesTakenBySeat !== undefined && (gameState as any)?.queenOfSpadesTakenBySeat !== null)
  );

  const isJackPlayed = Boolean(
    gameState?.jackOfDiamondsPlayed ??
    ((gameState as any)?.jackOfDiamondsTakenBySeat !== undefined && (gameState as any)?.jackOfDiamondsTakenBySeat !== null)
  );

  const passDirectionLabel = React.useMemo(() => {
    switch (passingDir) {
      case 'LEFT':
        return 'PASS: ⬅';
      case 'RIGHT':
        return 'PASS: ➡';
      case 'ACROSS':
        return 'PASS: ⬆';
      case 'HOLD':
      case 'NONE':
      default:
        return 'PASS: ⏸';
    }
  }, [passingDir]);

  // Determine target score for match end / win (50 or 100 pts)
  const targetScore = React.useMemo(() => {
    if (typeof gameState?.targetScore === 'number' && gameState.targetScore > 0) {
      return gameState.targetScore;
    }
    const cfgScore = gameState?.gameConfig?.scoring?.targetScore;
    if (typeof cfgScore === 'number' && cfgScore > 0) {
      return cfgScore;
    }
    const extTarget = (gameState?.gameExt as any)?.targetScore;
    if (typeof extTarget === 'number' && extTarget > 0) {
      return extTarget;
    }
    const matchType = String((gameState as any)?.matchType || gameState?.gameType || (gameState as any)?.cardGameType || '');
    if (matchType.includes('50') || matchType === '50_PTS') {
      return 50;
    }
    return 100;
  }, [gameState?.targetScore, gameState?.gameConfig?.scoring?.targetScore, (gameState?.gameExt as any)?.targetScore, (gameState as any)?.matchType, gameState?.gameType, (gameState as any)?.cardGameType]);

  // Keep HUD visible during PLAYING, TRICK_END, TRICK_RESOLVED, and HAND_END phases so status remains visible
  const phase = gameState?.phase;
  const isVisiblePhase = isPlayPhase || phase === 'PLAYING' || phase === 'TRICK_END' || phase === 'TRICK_RESOLVED' || phase === 'HAND_END';

  if (!isVisiblePhase) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: -12, y: -6, scale: 0.95 }}
        animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
        exit={{ opacity: 0, x: -12, y: -6, scale: 0.95 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="fixed top-2.5 left-4 sm:top-3.5 sm:left-5 z-[60] pointer-events-none select-none"
      >
        <div
          data-testid="hearts-status-hud"
          className="relative h-[38px] sm:h-[42px] overflow-hidden bg-gradient-to-b from-[#1a0a14]/95 via-[#120810]/98 to-[#080306]/98 backdrop-blur-xl rounded-xl sm:rounded-2xl px-2.5 sm:px-3 py-1 flex items-center gap-2 sm:gap-2.5 shadow-[0_12px_36px_rgba(0,0,0,0.85),0_4px_16px_rgba(0,0,0,0.6),inset_0_1px_0_0_rgba(255,255,255,0.2)] border border-rose-500/30"
        >
          {/* Luxury glass top edge inner highlight reflection */}
          <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-rose-300/40 to-transparent pointer-events-none" />

          {/* Hidden game header for tests and accessibility */}
          <span data-testid="hearts-game-header" className="sr-only">
            HEARTS
          </span>

          {/* Section 1: Hearts Broken Status Icon */}
          <div className="flex items-center justify-center shrink-0">
            {effectiveHeartsBroken ? (
              <div
                data-testid="hearts-broken-indicator"
                title="Hearts Broken (Hearts may be led)"
                className="flex items-center justify-center p-1 rounded-lg bg-rose-500/15 border border-rose-500/40 shadow-[0_0_10px_rgba(244,63,94,0.4)]"
              >
                <HeartCrack className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-rose-400 shrink-0 animate-pulse drop-shadow-[0_0_6px_rgba(244,63,94,0.8)]" />
                <span className="sr-only">♥ BROKEN</span>
              </div>
            ) : (
              <div
                data-testid="hearts-not-broken-indicator"
                title="Hearts Not Broken"
                className="flex items-center justify-center p-1 rounded-lg bg-white/5 border border-white/10"
              >
                <Heart className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-rose-500 fill-rose-500 drop-shadow-[0_0_6px_rgba(244,63,94,0.6)] shrink-0" />
                <span className="sr-only">♥ NOT BROKEN</span>
              </div>
            )}
          </div>

          {/* Vertical Separator 1 */}
          <div className="w-[1px] self-stretch bg-white/15 my-1" />

          {/* Section 2: Queen of Spades (and Jack of Diamonds) Badges with X/Slash */}
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Queen of Spades Icon Badge */}
            {isQueenPlayed ? (
              <div
                data-testid="queen-broken-indicator"
                title="Queen of Spades has been played (+13 taken)"
                className="relative flex items-center justify-center px-1.5 py-0.5 rounded bg-slate-900/90 border border-indigo-500/40 opacity-70 shadow-sm"
              >
                <span className="text-[11px] sm:text-xs font-black font-mono text-indigo-300">
                  Q♠
                </span>
                {/* Red/White X / Slash across Queen */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-[110%] h-[2px] bg-rose-500 rotate-45 rounded-full shadow-[0_0_4px_rgba(244,63,94,0.9)]" />
                  <div className="w-[110%] h-[2px] bg-rose-500 -rotate-45 rounded-full shadow-[0_0_4px_rgba(244,63,94,0.9)] absolute" />
                </div>
                <span className="sr-only">♠ QUEEN OUT</span>
              </div>
            ) : (
              <div
                data-testid="queen-safe-indicator"
                title="Queen of Spades is still in play (+13)"
                className="flex items-center justify-center px-1.5 py-0.5 rounded bg-slate-900 border border-white/20 shadow-[0_0_6px_rgba(99,102,241,0.3)]"
              >
                <span className="text-[11px] sm:text-xs font-black font-mono text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.7)]">
                  Q♠
                </span>
                <span className="sr-only">♠ QUEEN IN PLAY</span>
              </div>
            )}

            {/* Jack of Diamonds Icon Badge (if enabled) */}
            {isJackOfDiamondsEnabled && (
              isJackPlayed ? (
                <div
                  data-testid="jack-played-indicator"
                  title="Jack of Diamonds has been played (-10 taken)"
                  className="relative flex items-center justify-center px-1.5 py-0.5 rounded bg-amber-950/40 border border-amber-500/30 opacity-70 shadow-sm"
                >
                  <span className="text-[11px] sm:text-xs font-black font-mono text-amber-300">
                    J♦
                  </span>
                  {/* Red/White X / Slash across Jack */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-[110%] h-[2px] bg-rose-500 rotate-45 rounded-full shadow-[0_0_4px_rgba(244,63,94,0.9)]" />
                    <div className="w-[110%] h-[2px] bg-rose-500 -rotate-45 rounded-full shadow-[0_0_4px_rgba(244,63,94,0.9)] absolute" />
                  </div>
                  <span className="sr-only">♦ JACK OUT (-10)</span>
                </div>
              ) : (
                <div
                  data-testid="jack-in-play-indicator"
                  title="Jack of Diamonds is still in play (-10 bonus)"
                  className="flex items-center justify-center px-1.5 py-0.5 rounded bg-amber-950/60 border border-amber-400/60 shadow-[0_0_8px_rgba(245,158,11,0.4)]"
                >
                  <span className="text-[11px] sm:text-xs font-black font-mono text-amber-300 drop-shadow-[0_0_4px_rgba(245,158,11,0.8)]">
                    J♦
                  </span>
                  <span className="sr-only">♦ JACK IN PLAY</span>
                </div>
              )
            )}
          </div>

          {/* Vertical Separator 2 */}
          <div className="w-[1px] self-stretch bg-white/15 my-1" />

          {/* Section 3: Passing Direction */}
          <div
            data-testid="hearts-hud-details"
            className="flex items-center gap-1 text-[10px] sm:text-[11px] font-black font-mono uppercase tracking-wider text-rose-200/90 leading-none"
          >
            <span>{passDirectionLabel}</span>
          </div>

          {/* Vertical Separator 3 */}
          <div className="w-[1px] self-stretch bg-white/15 my-1" />

          {/* Section 4: Target Score / Win Target */}
          <div
            data-testid="hearts-target-score"
            className="flex items-center gap-1 text-[10px] sm:text-[11px] font-black font-mono uppercase tracking-wider text-amber-300/90 leading-none"
          >
            <span className="text-white/40 font-bold hidden sm:inline">WIN:</span>
            <span>{targetScore} PTS</span>
          </div>

          {/* Hidden Points Summary (Preserved for tests) */}
          <div data-testid="hearts-points-summary" className="sr-only">
            PTS: P1:{seatPoints[0] ?? 0} • P2:{seatPoints[1] ?? 0} • P3:{seatPoints[2] ?? 0} • P4:{seatPoints[3] ?? 0}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

